import React, { useState } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { ai } from "../../lib/api";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "general", l: "General" },
  { id: "story", l: "Story" },
  { id: "character", l: "Character" },
  { id: "enemy", l: "Enemy" },
  { id: "boss", l: "Boss" },
  { id: "weapon", l: "Weapon" },
  { id: "mechanic", l: "Mechanic" },
  { id: "level", l: "Level" },
];

export default function AIPanel({ project }) {
  const { t, lang } = useI18n();
  const [prompt, setPrompt] = useState("");
  const [category, setCategory] = useState("general");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setOutput("");
    try {
      const res = await ai.ideas({
        prompt,
        category,
        language: lang,
        project_id: project.project_id,
      });
      setOutput(res.text || "");
    } catch (e) {
      toast.error(t("toast.aiFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 mb-4 flex items-center gap-2">
        <Sparkle size={14} weight="fill" /> {t("ai.title")} — Claude Sonnet 4.5
      </div>
      <div className="border border-zinc-800 bg-zinc-900 p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">CATEGORY</div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="ai-category-select" className="bg-zinc-950 border-zinc-800 rounded-none w-48 font-mono text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none font-mono">
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          data-testid="ai-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder={t("ai.placeholder")}
          className="bg-zinc-950 border-zinc-800 rounded-none font-mono text-sm"
        />
        <Button
          data-testid="ai-send-btn"
          onClick={handleSend}
          disabled={loading || !prompt.trim()}
          className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide gap-2"
        >
          <Sparkle size={14} weight="fill" /> {loading ? "..." : t("ai.send")}
        </Button>
      </div>
      {output && (
        <div className="mt-6 border border-zinc-800 bg-zinc-950 p-5 whitespace-pre-wrap font-mono text-sm text-zinc-200 leading-relaxed" data-testid="ai-output">
          {output}
        </div>
      )}
    </div>
  );
}
